import { useState } from 'react'
import { Box, Button, Dialog, Typography, useMediaQuery } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import CheckIcon from '@mui/icons-material/Check'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { useTranslation } from 'react-i18next'
import StudyQr from '@/components/researcher/StudyQr'
import { DeskInput, Field, Tag } from './desk'
import { LINE, outlinedBtnSx, primaryBtnSx } from './deskStyles'

type LaunchedDialogProps = {
  /** The respondent share link; null while no study has been launched. */
  shareLink: string | null
  onClose: () => void
}

/** Post-launch hand-off: the QR to print on the left, the link and the way
 *  into the study on the right. Both editions close their launch with it. */
export default function LaunchedDialog({ shareLink, onClose }: LaunchedDialogProps) {
  const theme = useTheme()
  const c = theme.affexy.colors
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const { t } = useTranslation('dashboard')
  const [copied, setCopied] = useState(false)

  const copyShareLink = async () => {
    if (!shareLink) return
    try {
      await navigator.clipboard.writeText(shareLink)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1800)
    } catch {
      /* clipboard unavailable */
    }
  }

  return (
    <Dialog
      open={!!shareLink}
      onClose={onClose}
      maxWidth={false}
      fullScreen={fullScreen}
      slotProps={{ paper: { sx: { width: { sm: 700 }, borderRadius: fullScreen ? 0 : '12px' } } }}
    >
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '190px 1fr' }, gap: 3.25, p: 3.5 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'center', sm: 'flex-start' } }}>
          {shareLink && (
            <Box sx={{ p: 1.5, border: `1px solid ${LINE}`, borderRadius: '10px', '& button': { mt: 1 } }}>
              <StudyQr value={shareLink} size={160} fileName={t('wizard.launched.qrFileName')} />
            </Box>
          )}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Tag tone="ok">{t('wizard.desk.launched.active')}</Tag>
          <Typography sx={{ fontSize: 21, fontWeight: 900, letterSpacing: '-.01em', mt: 1, mb: 0.5 }}>{t('wizard.desk.launched.title')}</Typography>
          <Typography sx={{ fontSize: 13.5, color: c.ink56, lineHeight: 1.5 }}>{t('wizard.launched.body')}</Typography>
          <Box sx={{ mt: 2.25 }}>
            <Field label={t('wizard.desk.launched.link')}>
              <DeskInput
                value={shareLink ?? ''}
                slotProps={{ input: { readOnly: true }, htmlInput: { 'aria-label': t('wizard.desk.launched.link') } }}
                onFocus={(e) => e.target.select()}
                suffix={
                  <Button
                    size="small"
                    onClick={copyShareLink}
                    startIcon={copied ? <CheckIcon sx={{ fontSize: 15 }} /> : <ContentCopyIcon sx={{ fontSize: 15 }} />}
                    sx={{ ...outlinedBtnSx, height: 28, mr: -0.75 }}
                  >
                    {t(copied ? 'wizard.launched.copied' : 'wizard.launched.copy')}
                  </Button>
                }
              />
            </Field>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, mt: 2.75, flexWrap: 'wrap' }}>
            {shareLink && (
              <Button
                onClick={() => window.open(shareLink, '_blank', 'noopener')}
                endIcon={<OpenInNewIcon sx={{ fontSize: 15 }} />}
                sx={{ ...outlinedBtnSx, height: 34 }}
              >
                {t('wizard.desk.launched.openAsRespondent')}
              </Button>
            )}
            <Box sx={{ flex: 1 }} />
            <Button variant="contained" onClick={onClose} sx={{ ...primaryBtnSx, height: 34 }}>
              {t('wizard.desk.launched.goToStudy')}
            </Button>
          </Box>
        </Box>
      </Box>
    </Dialog>
  )
}
